import { SessionService } from '../services/SessionService';
import { ApiService } from '../services/ApiService';
import { DOMElement } from '../utils/DOMElement';
import { Component } from './Component';
import { CaptchaError } from '../errors/CaptchaError';

/**
 * Captcha stage component.
 */
export class Captcha extends Component {
  /**
   * String of the DOM element containing the step title.
   *
   * @type {string}
  */
  static titleDivDOMSelector = '#captchaStepTitle';

  /**
   * String of the DOM element containing the captcha error.
   */
  static captchaErrorDOMSelector = '#captchaError';

  /**
   * String of the DOM element of the indication list.
   *
   * @type {string}
   */
  static indicationListDOMSelector = '.indications';

  /**
   * String of the DOM element of the indication thumbnail image.
   *
   * @type {string}
   */
  static indicationThumbnailDOMSelector = '.indication-thumbnail';

  /**
   * String of the DOM element of the captcha items container.
   *
   * @type {string}
   */
  static rowsDOMSelector = '.rows';

  /**
   * String of the DOM element of the clickable captcha items.
   *
   * @type {string}
   */
  static selectCaptchaRowDOMSelector = '.select-captcha-row-';

  /**
   * String of the DOM element of the area containing selected image.
   *
   * @type {string}
   */
  static selectedCaptchaElementDOMSelector = '.matches.item-';

  /**
   * String of the DOM element of the submit button.
   *
   * @type {string}
   */
  static submitButtonDOMSelector = '#btnSubmit';

  /**
   * Prefix of the string of the DOM element of the indication list item.
   *
   * @type {string}
   */
  static indicationLabelDOMSelector = '#indication-';

  /**
   * String of the attribute name of the captcha length.
   *
   * @type {string}
   */
  static captchaLengthAttributeName = 'data-captcha-length';

  /**
   * String of attribute name of a captcha row value.
   *
   * @type {string}
   */
  static captchaRowValueAttributeName = 'data-captcha-row-value';

  /**
   * String of the attribute of a captcha row index.
   *
   * @type {string}
   */
  static captchaRowIndexAttributeName = 'data-captcha-row-index';

  /**
   * String of the class 'disabled'.
   *
   * @type {string}
   */
  static classDisabled = 'disabled';

  /**
   * String of the class 'next'.
   *
   * @type {string}
   */
  static classNext = 'next';

  /**
   * String of the class 'current'.
   *
   * @type {string}
   */
  static classCurrent = 'current';

  /**
   * String of the class 'previous'.
   *
   * @type {string}
   */
  static classPrevious = 'previous';

  /**
   * String of the class 'last'.
   *
   * @type {string}
   */
  static classLast = 'last';

  /**
   * String of a 'div' DOM element.
   *
   * @type {string}
   */
  static elementDiv = 'div';

  /**
   * String of a 'img' DOM element.
   *
   * @type {string}
   */
  static elementImg = 'img';

  /**
   * Constructor.
   *
   * @param {string} container - The stage container.
   */
  constructor(container) {
    super(container);
    this.initialize(container);
  }

  /**
   * Initialization of the Captcha element.
   *
   * @param {string} container - The stage container.
   */
  initialize(container) {
    this.error = new CaptchaError();
    this.captchaContainer = document.querySelector(container);
    this.indicationUl = document.querySelector(Captcha.indicationListDOMSelector);
    this.captchaRowItems = document.querySelector(Captcha.rowsDOMSelector).childNodes;
    this.captchaLength = this.indicationUl.attributes[Captcha.captchaLengthAttributeName].value;
    this.submitButton = document.querySelector(Captcha.submitButtonDOMSelector);
    this.currentStep = 1;
    this.value = [];
    this.nbClick = 0;
    this.titleDiv = document.querySelector(Captcha.titleDivDOMSelector);
    this.captchaErrorDiv = document.querySelector(Captcha.captchaErrorDOMSelector);
  }

  /**
   * Calculate the X position of the target DOM element.
   *
   * @param {obj} targetElem - The target DOM element.
   * @param {number} scrollLeft - The scroll left of the page.
   * @returns {number} The X position of the target.
   */
  getTargetEndPositionX(targetElem, scrollLeft) {
    return Math.round(((targetElem ? targetElem.getBoundingClientRect().left : 0) + scrollLeft) - (targetElem ? targetElem.offsetWidth / 2 : 0));
  }

  /**
   * Calculate the X position of the target DOM element.
   *
   * @param {obj} targetElem - The target DOM element.
   * @param {number} scrollTop - The scroll left of the page.
   * @returns {number} The Y position of the target.
   */
  getTargetEndPositionY(targetElem, scrollTop) {
    return Math.round(((targetElem ? targetElem.getBoundingClientRect().top : 0) + scrollTop) - (targetElem ? targetElem.offsetHeight / 2 : 0));
  }

  /**
   * Row item click event callback.
   * Add the value to the value array and animate the images.
   *
   * @param {number} rowIdx - The index of the row item.
   * @param {number} value - The value of the row item.
   */
  selectCaptchaItem(rowIdx, value) {
    this.nbClick += 1;
    if (this.nbClick > this.captchaLength) {
      this.nbClick = parseInt(this.captchaLength, 10) + 1;
      return;
    }
    if (this.currentStep > this.captchaLength) {
      return;
    }
    const initialImg = document.querySelector(Captcha.selectCaptchaRowDOMSelector + rowIdx);
    const targetElem = document.querySelector(Captcha.selectedCaptchaElementDOMSelector + this.currentStep);

    // Inserting value
    this.value.push(value);

    const clickedImg = document.querySelector(`.select-captcha-row-${rowIdx}`).firstChild;

    const scrollTop = DOMElement.getPageYOffset();
    const scrollLeft = DOMElement.getPageXOffset();

    const start = {
      xStart: initialImg.getBoundingClientRect().left + scrollLeft,
      yStart: initialImg.getBoundingClientRect().top + scrollTop,
    };
    const end = {
      xEnd: this.getTargetEndPositionX(targetElem, scrollLeft),
      yEnd: this.getTargetEndPositionY(targetElem, scrollTop),
    };

    const transitionImg = document.createElement(Captcha.elementImg);
    transitionImg.src = clickedImg.attributes.src.value;
    document.body.appendChild(transitionImg);

    const matchImgDiv = document.createElement(Captcha.elementDiv);
    DOMElement.addClass('indication-thumbnail', matchImgDiv);

    const matchImg = transitionImg.cloneNode();

    const removeIndication = document.createElement('button');
    DOMElement.addClasses([
      'remove-indication',
      'captcha-icon-Modifier_delete',
    ], removeIndication);
    removeIndication.setAttribute('type', 'button');
    removeIndication.setAttribute('aria-label', 'effacer la dernière sélection');

    matchImg.onclick = (clickEvent) => {
      this.removeIndication(clickEvent);
    };
    removeIndication.onclick = (clickEvent) => {
      this.removeIndication(clickEvent);
    };

    DOMElement.appendChildren([
      matchImg,
      removeIndication,
    ], matchImgDiv);

    DOMElement.setSize('100%', '100%', matchImg);
    DOMElement.setLeft(`${start.xStart}px`, matchImg);
    DOMElement.setTop(`${start.yStart}px`, matchImg);

    DOMElement.setSize(initialImg.width, initialImg.height, transitionImg);
    DOMElement.setPosition(`${start.xStart}px`, `${start.yStart}px`, transitionImg);
    DOMElement.setPositionType('absolute', transitionImg);

    const xStep = Math.abs(start.xStart - end.xEnd) / 45;
    const yStep = Math.abs(start.yStart - end.yEnd) / 45;

    let baseX = start.xStart;
    let baseY = start.yStart;
    const baseSize = transitionImg.width;
    const endSize = targetElem ? targetElem.offsetWidth : 100;
    const sizeStep = endSize / baseSize / 45;
    let scale = 1;
    let id;

    /**
     * Function called at the end of the animation of a picture
     * when a row item is selected.
     */
    const endAnimation = () => {
      const match = document.querySelector(Captcha.selectedCaptchaElementDOMSelector + this.currentStep);
      DOMElement.setSize(endSize, endSize, transitionImg);
      DOMElement.setPosition(`${end.xEnd}px`, `${end.yEnd}px`, transitionImg);

      transitionImg.remove();
      if (match) {
        match.appendChild(matchImgDiv);
      }

      const currentIndication = document.querySelector(Captcha.indicationLabelDOMSelector + this.currentStep);
      const previousIndication = document.querySelector(Captcha.indicationLabelDOMSelector + (parseInt(this.currentStep, 10) - 1));
      if (previousIndication !== null && DOMElement.hasClass(Captcha.classLast, previousIndication)) {
        DOMElement.removeClass(Captcha.classLast, previousIndication);
      }
      if (currentIndication) {
        DOMElement.addClasses([
          Captcha.classPrevious,
          Captcha.classLast,
        ], currentIndication);
        DOMElement.removeClass(Captcha.classCurrent, currentIndication);
      }

      const nextIndication = document.querySelector(Captcha.indicationLabelDOMSelector + (parseInt(this.currentStep, 10) + 1));
      if (nextIndication !== null) {
        DOMElement.removeClass(Captcha.classNext, nextIndication);
        DOMElement.addClass(Captcha.classCurrent, nextIndication);
      }

      this.currentStep += 1;
      if (this.currentStep > this.captchaLength) {
        DOMElement.removeClass(Captcha.classDisabled, this.submitButton);
      }
      return clearInterval(id);
    };

    /**
     * Animation function that translates and scales the image when
     * an row item is clicked.
     */
    const frame = () => {
      if (baseX === end.xEnd && baseY === end.yEnd) {
        return endAnimation();
      }
      if (baseX < end.xEnd) {
        baseX += xStep;
        if (baseX > end.xEnd) {
          return endAnimation();
        }
      } else if (baseX > end.xEnd) {
        baseX -= xStep;
        if (baseX < end.xEnd) {
          return endAnimation();
        }
      }
      if (baseY < end.yEnd) {
        baseY += yStep;
        if (baseY > end.yEnd) {
          return endAnimation();
        }
      } else if (baseY > end.yEnd) {
        baseY -= yStep;
        if (baseY < end.yEnd) {
          return endAnimation();
        }
      }
      transitionImg.style.transform = `scale(${scale}, ${scale})`;
      scale -= sizeStep;
      DOMElement.setPosition(`${baseX}px`, `${baseY}px`, transitionImg);
      return true;
    };

    id = setInterval(frame, 1);
  }

  /**
   * Indication removal event callback.
   * Removes the value from the value array and the corresponding indication thumbnail.
   */
  removeIndication() {
    this.nbClick -= 1;
    if (this.nbClick <= 0) {
      this.nbClick = 0;
    }
    if (this.currentStep > this.captchaLength) {
      const indicationElement = this.indicationUl.childNodes[this.currentStep - 2];
      const prevIndicationElement = this.indicationUl.childNodes[this.currentStep - 3];
      DOMElement.removeClasses([
        Captcha.classLast,
        Captcha.classPrevious,
      ], indicationElement);
      DOMElement.addClass(Captcha.classCurrent, indicationElement);
      DOMElement.addClass(Captcha.classLast, prevIndicationElement);
      indicationElement.querySelector(Captcha.indicationThumbnailDOMSelector).remove();

      this.value.pop();
      DOMElement.addClass(Captcha.classDisabled, this.submitButton);
      this.currentStep -= 1;
      this.nbClick -= 1;
    } else {
      const indicationElement = this.indicationUl.childNodes[this.currentStep - 1];
      const prevIndicationElement = this.indicationUl.childNodes[this.currentStep - 2];

      DOMElement.removeClass(Captcha.classCurrent, indicationElement);
      DOMElement.addClass(Captcha.classNext, indicationElement);

      this.value.pop();
      if (this.currentStep > 1) {
        DOMElement.removeClasses([
          Captcha.classPrevious,
          Captcha.classLast,
        ], prevIndicationElement);
        DOMElement.addClass(Captcha.classCurrent, prevIndicationElement);

        if (this.currentStep > 2) {
          const prevLastIndicationElement = document.querySelector(`${Captcha.indicationLabelDOMSelector}${this.currentStep - 2}`);
          DOMElement.addClass(Captcha.classLast, prevLastIndicationElement);
          prevIndicationElement.lastChild.lastChild.remove();
        } else {
          document.querySelector(`${Captcha.indicationLabelDOMSelector}1`).lastChild.lastChild.remove();
        }
        this.currentStep -= 1;
      }
    }

    const previousButton = this.indicationUl.querySelector('.last button.remove-indication');

    if (previousButton !== null) {
      previousButton.focus();
    }
  }

  /**
   * Render template.
   *
   * @param {object} opts - The parameters to render the component.
   */
  render(opts) {
    if (opts) {
      super.render(opts);
    }
    this.attachListener(opts.isBack || false);
    // Tell the visually impaired that the page has changed
    super.announceNextPageLoad();
  }

  /**
   * Back event treatment for Identity.
   */
  back() {
    window.history.back();
  }

  /**
   * Hide the title and subtitle, then display the error message and
   * reinitializes the captcha with the new configuration.
   *
   * @param {boolean} requestPending - True if the request is pending, false otherwise.
   */
  displayErrorAndReinitialize(requestPending) {
    this.initialize('#stage');
    this.attachListener(false);
    this.error.handleInvalidCaptchaError();
    this.error.displayCaptchaError();
    this.titleDiv.style.display = 'none';
    if (requestPending !== true) {
      SessionService.restartSessionTimer();
    }
  }

  /**
   * Callback for when submit button is clicked.
   */
  onSubmit() {
    if (DOMElement.hasClass(Captcha.classDisabled, this.submitButton)) {
      return;
    }
    const opt = {
      url: ApiService.apiSchema.captcha.url,
      method: ApiService.apiSchema.captcha.method,
      data: {
        value: this.value,
      },
    };

    return Component.makeRequest(opt, undefined, true)
      .then((requestPending) => {
        this.displayErrorAndReinitialize(requestPending);
      })
      .catch(() => {});
  }

  /**
   * Hide Captcha error message and display title again.
   */
  hideCaptchaErrorIfExists() {
    if (this.captchaErrorDiv.classList.contains('disp')) {
      this.captchaErrorDiv.classList.add('hide');
      this.captchaErrorDiv.classList.remove('disp');
      this.titleDiv.removeAttribute('style');
    }
  }

  /**
   * Attach listener for the component.
   */
  attachListener() {
    this.submitButton.onclick = () => {
      this.onSubmit();
    };
    for (let i = 0; i < this.captchaRowItems.length; i += 1) {
      const { value } = this.captchaRowItems[i].firstChild.attributes[Captcha.captchaRowValueAttributeName];
      const rowIdx = this.captchaRowItems[i].firstChild.attributes[Captcha.captchaRowIndexAttributeName].value;
      this.captchaRowItems[i].firstChild.onclick = () => {
        this.selectCaptchaItem(rowIdx, value);
        this.hideCaptchaErrorIfExists();
      };
    }
  }
}
